import { AddressDtoSchema } from "@modules/address/addressDto"
import { BenchmarkTypeOf } from "@modules/benchmark/benchmarkTypes"
import { BoxTypeOf } from "@modules/box/boxTypes"
import { ChronoDtoSchema } from "@modules/chrono/services/chronoDtos"
import { PriceObjectDtoSchema } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { UserDtoSchema } from "@modules/profile/profileDtos"
import { ProfileTypeOf } from "@modules/profile/profileTypes"
import { ProgramBundleType, ProgramBundleTypeOf } from "@modules/programBundle/api/programBundleDto"
import { ProgramContentTypeOf, ProgramVisibility } from "@modules/programs/programTypes"
import { z } from "zod"

const ExploreBenchmarkItemDtoSchema = z.object({
  id: z.string(),
  type: z.literal(BenchmarkTypeOf),
  title: z.string(),
  category: z.string(),
  chrono: ChronoDtoSchema.nullable(),
  isFavorite: z.boolean(),
  rpe: z.number().nullable(),
})
const ExploreBoxItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: AddressDtoSchema,
  logo: HUImageDtoSchema,
  cover: HUImageDtoSchema,
  isSubscribed: z.boolean(),
  event: z.boolean().nullable(),
})
const ExploreProfileItemDtoSchema = z.object({
  id: z.string(),
  firstname: z.string(),
  lastname: z.string(),
  username: z.string(),
  avatar: HUImageDtoSchema.nullable(),
})
const ExploreProgramItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: HUImageDtoSchema.nullable(),
  owner: UserDtoSchema,
  visibility: z.nativeEnum(ProgramVisibility),
  subscribed: z.boolean(),
  subscriptionCount: z.number(),
  price: PriceObjectDtoSchema.nullable(),
  defaultHideSessionContent: z.boolean(),
  type: z.string(),
  exploreType: z.literal(ProgramContentTypeOf),
})

export const ExploreProgramBundleItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: HUImageDtoSchema.nullable(),
  owner: UserDtoSchema,
  visibility: z.nativeEnum(ProgramVisibility),
  subscribed: z.boolean(),
  subscriptionCount: z.number(),
  price: PriceObjectDtoSchema.nullable(),
  type: z.nativeEnum(ProgramBundleType),
  nbPrograms: z.number(),
  exploreType: z.literal(ProgramBundleTypeOf),
})

export const ItemExploreContentTypesDtoSchema = z.union([
  ExploreBenchmarkItemDtoSchema,
  ExploreProgramItemDtoSchema,
  ExploreBoxItemDtoSchema,
  ExploreProfileItemDtoSchema,
])

export const ExploreBaseContentDtoSchema = z.object({
  contentType: z.string(),
  content: ItemExploreContentTypesDtoSchema,
})

export const ExploreBenchmarkContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(BenchmarkTypeOf),
  content: ExploreBenchmarkItemDtoSchema,
})

export const ExploreProgamContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(ProgramContentTypeOf),
  content: ExploreProgramItemDtoSchema,
})

export const ExploreProgramBundleContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(ProgramBundleTypeOf),
  content: ExploreProgramBundleItemDtoSchema,
})

export const ExploreBoxContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(BoxTypeOf),
  content: ExploreBoxItemDtoSchema,
})

export const ExploreProfileContentDtoSchema = ExploreBaseContentDtoSchema.extend({
  contentType: z.literal(ProfileTypeOf),
  content: ExploreProfileItemDtoSchema,
})

export const ExploreContentDtoSchema = z.union([
  ExploreBenchmarkContentDtoSchema,
  ExploreProgamContentDtoSchema,
  ExploreProgramBundleContentDtoSchema,
  ExploreBoxContentDtoSchema,
  ExploreProfileContentDtoSchema,
])

export type ItemExploreContentTypesDto = z.infer<typeof ItemExploreContentTypesDtoSchema>
export type ExploreBaseContentDto = z.infer<typeof ExploreBaseContentDtoSchema>
export type ExploreBenchmarkContentDto = z.infer<typeof ExploreBenchmarkContentDtoSchema>
export type ExploreProgamContentDto = z.infer<typeof ExploreProgamContentDtoSchema>
export type ExploreProgramBundleDto = z.infer<typeof ExploreProgramBundleContentDtoSchema>
export type ExploreBoxContentDto = z.infer<typeof ExploreBoxContentDtoSchema>
export type ExploreProfileContentDto = z.infer<typeof ExploreProfileContentDtoSchema>
export type ExploreContentDto = z.infer<typeof ExploreContentDtoSchema>

export type ExploreBenchmarkItemDto = z.infer<typeof ExploreBenchmarkItemDtoSchema>
export type ExploreBoxItemDto = z.infer<typeof ExploreBoxItemDtoSchema>
export type ExploreProfileItemDto = z.infer<typeof ExploreProfileItemDtoSchema>
export type ExploreProgramItemDto = z.infer<typeof ExploreProgramItemDtoSchema>
export type ExploreProgramBundleItemDto = z.infer<typeof ExploreProgramBundleItemDtoSchema>
